import { useMemo, useRef, useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Building2, BookUser } from "lucide-react";
import type { ShipperAddress } from "@/hooks/useShipperAddresses";

interface Props {
  value: string;
  addresses: ShipperAddress[];
  disabled?: boolean;
  placeholder?: string;
  onChange: (value: string) => void;
  onSelect: (addr: ShipperAddress) => void;
}

/**
 * Type-ahead autocomplete over the shipper's saved address book.
 * Matches partial, case-insensitive across label, facility name, city, and state.
 */
export function AddressBookAutocomplete({
  value, addresses, disabled, placeholder, onChange, onSelect,
}: Props) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);

  const q = value.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return addresses.slice(0, 8);
    return addresses
      .filter((a) => {
        const hay = [a.label, a.name, a.city, a.state, a.address, a.postal_code]
          .filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 8);
  }, [addresses, q]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pick = (a: ShipperAddress) => {
    onSelect(a);
    setOpen(false);
    setHi(-1);
  };

  return (
    <div ref={wrapRef} className="relative">
      <Input
        value={value}
        disabled={disabled}
        placeholder={placeholder ?? "Type facility name, city, or label…"}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHi(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open || matches.length === 0) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, matches.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
          else if (e.key === "Enter" && hi >= 0) { e.preventDefault(); pick(matches[hi]); }
          else if (e.key === "Escape") setOpen(false);
        }}
      />
      {open && !disabled && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg max-h-72 overflow-auto">
          {matches.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground flex items-center gap-1.5">
              <BookUser className="h-3.5 w-3.5" /> No matches — type the full address below.
            </div>
          ) : (
            <>
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
                From your address book ({matches.length})
              </div>
              {matches.map((a, idx) => (
                <button
                  type="button"
                  key={a.id}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(a)}
                  onMouseEnter={() => setHi(idx)}
                  className={cn(
                    "w-full text-left px-3 py-2 text-sm flex items-start gap-2 hover:bg-muted",
                    hi === idx && "bg-brand-teal/10"
                  )}
                >
                  <Building2 className="h-4 w-4 mt-0.5 text-brand-teal shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{a.label}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {[a.name, a.address, [a.city, a.state].filter(Boolean).join(", "), a.postal_code]
                        .filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  {a.use_count > 0 && (
                    <span className="text-[10px] text-muted-foreground shrink-0 mt-1">{a.use_count}×</span>
                  )}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
